import { Component, forwardRef, Input, ViewChild, ElementRef, OnInit, Output, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, EMPTY as empty, BehaviorSubject } from 'rxjs';
import { map, first, finalize } from 'rxjs/operators';

import { FileUploadInfo } from '../va-file-upload/file-upload-info';
import { FileUploadService } from '../va-file-upload/file-upload.service';
import { FileControlValueAccessor } from '../va-file-upload/file-control-value-accessor';
import { MediaUploader } from './media-upload.interface';
import { TranslateService } from '@ngx-translate/core';

const STRING_CONTROL_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ImageUploaderComponent),
  multi: true,
};

@Component({
  selector: 'forms-image-uploader',
  styleUrls: ['./image-upload.component.scss'],
  templateUrl: './image-upload.component.html',
  providers: [STRING_CONTROL_ACCESSOR],
})
export class ImageUploaderComponent extends FileControlValueAccessor<FileUploadInfo> implements OnInit {
  @Input() uploadUrl: string;
  @Input() showClear = true;
  @Input() showMaxFileSize = true;
  @Input() customUploader: MediaUploader;
  @Input() imageNoun = 'Image';
  @Input() allowedFileExtensions: string[] = [];
  @Input() acceptedMimeTypes = 'image/*';
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
  @ViewChild('dropBox', { static: true }) dropBox: ElementRef;
  @Output() imageUploadError = new EventEmitter<Error>();

  @Input()
  set maxFileSize(value: number) {
    this._maxFileSize = value;
    this._maxFileSizeMb = this.maxFileSize / 1024 / 1024;
  }
  get maxFileSize(): number {
    return this._maxFileSize;
  }
  _maxFileSize: number;
  _maxFileSizeMb: number;

  dragover = false;
  isUploading$$ = new BehaviorSubject<boolean>(false);

  constructor(private fileUploadService: FileUploadService, private translateService: TranslateService) {
    super();
    this.innerValue = null;
  }

  maxFileSizeMb(): string {
    const sizeInMb = this.maxFileSize / 1024 / 1024;
    return `Maximum file size ${sizeInMb.toFixed(2)} MB`;
  }

  ngOnInit(): void {
    ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].map((on) => {
      this.dropBox.nativeElement.addEventListener(on, (e: any) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });
  }

  private validate(file: File): Error | null {
    const fileType = file.type.split('/').slice(-1)[0].toLowerCase();
    const extensions = this.allowedFileExtensions.map((type) => type.toLowerCase());

    if (!file) {
      return new Error(this.translateService.instant('FRONTEND.FORMS.IMAGE_UPLOAD.IMAGE_NOT_DETECTED'));
    }
    if (!file.type.match('image/.*') || (extensions.length > 0 && extensions.indexOf(fileType) < 0)) {
      return new Error(
        this.translateService.instant('FRONTEND.FORMS.IMAGE_UPLOAD.UNSUPPORTED_FILE_TYPE', { fileType: file.type }),
      );
    }
    if (this.maxFileSize && file.size > this.maxFileSize) {
      return new Error(this.translateService.instant(`FRONTEND.FORMS.IMAGE_UPLOAD.FILE_TOO_LARGE`));
    }
    return null;
  }
  uploadFiles(file: File): void {
    const err = this.validate(file);
    if (err) {
      this.handleError(err);
      return;
    }

    this.isUploading$$.next(true);
    let uploadResult: Observable<FileUploadInfo>;
    if (this.customUploader) {
      uploadResult = this.customUploader
        .upload(file)
        .pipe(map((result) => ({ ...result, url: result.url, name: file.name })));
    } else if (this.uploadUrl) {
      uploadResult = this.fileUploadService.upload(this.uploadUrl, file);
    } else {
      uploadResult = empty;
      console.error('No upload url set');
      this.handleError(new Error(this.translateService.instant('FRONTEND.FORMS.IMAGE_UPLOAD.NO_UPLOAD_URL_SET')));
    }

    uploadResult
      .pipe(
        first(),
        finalize(() => {
          this.isUploading$$.next(false);
        }),
      )
      .subscribe(
        (fileUpload) => {
          this.handleFileUploaded(fileUpload);
        },
        (errorMessage) => {
          this.handleError(new Error(errorMessage));
        },
      );
  }

  handleFileUploaded(info: FileUploadInfo): void {
    this.value = info;
  }

  handleError(err: Error): void {
    this.imageUploadError.emit(err);
  }

  handleInputChange(): void {
    this.uploadFiles(this.fileInput.nativeElement.files[0]);
  }

  handleDropEvent(event: DragEvent): void {
    this.dragover = false;
    if (this.disabled) {
      return;
    }
    if (event.dataTransfer) {
      this.uploadFiles(event.dataTransfer.files[0]);
    } else {
      this.handleError(
        new Error(this.translateService.instant('FRONTEND.FORMS.IMAGE_UPLOAD.DRAG_AND_DROP_UNSUPPORTED')),
      );
    }
  }

  clearValue(): void {
    this.value = null;
  }
}
